/**
 * ADR Sensor - Punto de entrada principal
 * 
 * Coordina la inicialización del sensor, gestión de configuración, monitores de detección
 * (XSS, CSRF) y el sistema de transporte (Worker o Dispatcher). Expone la API pública
 * del sensor a través de window.IRIS_ADR (IIFE) o como módulo ESM.
 */

import Config from './core/config.js';
import Dispatcher from './transport/dispatcher.js';
import SensorIntegrity from './core/sensor-integrity.js';
import SessionTokenManager from './auth/session-token-manager.js';
import XSSMonitor from './detectors/xss-monitor.js';
import CSRFMonitor from './detectors/csrf-monitor.js';
import InjectionMonitor from './detectors/injection-monitor.js';
import WorkerManager from './worker/worker-manager.js';
import ScriptIntegrityReporter from './integrity/script-integrity-reporter.js';

class ADR {
	constructor() {
		this.config = new Config();
		this.transport = null;
		this.workerManager = null;
		this.sensorIntegrity = null;
		this.sessionTokenManager = null;
		this.xssMonitor = null;
		this.csrfMonitor = null;
		this.injectionMonitor = null;
		this.scriptIntegrityReporter = null;
		this.initialized = false;
	}

	async init(config = {}) {
		if (this.initialized) {
			return this.config.getAll();
		}
		if (typeof config === 'string') {
			config = { profileId: config };
		}
		await this.config.init(config);

		this.sensorIntegrity = new SensorIntegrity(this.config, this.config.logger);
		try {
			const sensorHash = await this.sensorIntegrity.getSensorHash();
			if (sensorHash) {
				this.config.update({ sensorHash });
			}
		} catch {
		}
		this.config.lock();

		this.sessionTokenManager = new SessionTokenManager(this.config, {
			sensorIntegrity: this.sensorIntegrity,
			logger: this.config.logger
		});

		this.workerManager = new WorkerManager(this.config, {
			sensorIntegrity: this.sensorIntegrity,
			tokenManager: this.sessionTokenManager
		});
		await this.workerManager.init();

		if (this.workerManager && this.workerManager.active) {
			this.transport = this.workerManager;
		} else {
			this.transport = new Dispatcher(this.config, {
				sensorIntegrity: this.sensorIntegrity,
				tokenManager: this.sessionTokenManager
			});
			this.transport.start();
		}

		if (this.config.get('xssDetectionEnabled')) {
			this.xssMonitor = new XSSMonitor();
			this.xssMonitor.start(this.transport, this.config);
		}

		if (this.config.get('csrfDetectionEnabled')) {
			this.csrfMonitor = new CSRFMonitor(this.config);
			this.csrfMonitor.start(this.transport);
		}

		if (this.config.get('injectionDetectionEnabled')) {
			this.injectionMonitor = new InjectionMonitor();
			this.injectionMonitor.start(this.transport, this.config);
		}

		if (this.config.get('scriptIntegrityEnabled')) {
			this.scriptIntegrityReporter = new ScriptIntegrityReporter(this.config, {
				tokenManager: this.sessionTokenManager,
				sensorIntegrity: this.sensorIntegrity
			});
			this.scriptIntegrityReporter.start();
		}

		this.initialized = true;
		return this.config.getAll();
	}

	flush() {
		if (!this.initialized) {
			throw new Error('ADR not initialized. Call init() first.');
		}
		return this.transport.flush();
	}

	getQueueStatus() {
		if (!this.initialized) return null;
		if (this.transport && this.transport.getQueueStatus) {
			return this.transport.getQueueStatus();
		}
		return null;
	}

	getConfig() {
		return this.config.getAll();
	}

	isInitialized() {
		return this.initialized;
	}

	async shutdown() {
		if (this.workerManager && this.workerManager.active) {
			await this.workerManager.flush();
			this.workerManager.stop();
		} else if (this.transport && this.transport instanceof Dispatcher) {
			this.transport.stop();
			await this.transport.flush();
		}
		if (this.xssMonitor) {
			this.xssMonitor.stop();
		}
		if (this.csrfMonitor) {
			this.csrfMonitor.stop();
		}
		if (this.injectionMonitor) {
			this.injectionMonitor.stop();
		}
		if (this.scriptIntegrityReporter) {
			this.scriptIntegrityReporter.stop();
		}
		this.initialized = false;
	}
}

const instance = new ADR();

if (typeof window !== 'undefined') {
	window.IRIS_ADR = {
		init: (cfg) => instance.init(cfg),
		flush: () => instance.flush(),
		getQueueStatus: () => instance.getQueueStatus(),
		getConfig: () => instance.getConfig(),
		isInitialized: () => instance.isInitialized(),
		shutdown: () => instance.shutdown(),
		getToken: () => instance.sessionTokenManager
			? instance.sessionTokenManager.getToken()
			: Promise.reject(new Error('Sensor not initialized — call init() first')),
	};

	// Auto-inicialización si existe window.__ADR_CONFIG
	if (window.__ADR_CONFIG && typeof window.__ADR_CONFIG === 'object') {
		instance.init(window.__ADR_CONFIG).catch(() => {});
	}
}

export default instance;


